import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Slider } from '../ui/slider';
import { Download, FileImage, FileText, Image, Loader2, CheckCircle2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useToast } from '../../hooks/use-toast';
import {
  getPagesToDownload,
  validatePageRange,
  getSafeExportDimensions,
  getDownloadFilename,
} from '../../utils/downloadUtils';
import { inlineClipPathsForExport } from '../../utils/exportClipPath';
import { inlineObjectFitForExport, pairImagesForExport, stripSvgRootFiltersForExport } from '../../utils/exportObjectFit';
import { normalizeTextMetricsForExport } from '../../utils/exportTextNormalize';
import { serializeTextElementsForExport } from '../../utils/exportTextSerialize';
import { captureExportState, assertExportParity, verifySnapshotFresh } from '../../utils/exportSnapshot';
import { awaitExportStability, installReplayMode } from '../../utils/exportStabilize';
import { computeExportVisualDiff, diffExportByElement } from '../../utils/exportVisualDiff';
import { fingerprintCanvas } from '../../utils/exportRegressionScenes';
import { autoRetryFailingTextElements } from '../../utils/exportTextAutoRetry';
import { prepareEmbeddedFontCss, getCachedEmbeddedFontCss, getEmbeddedFontCssFor } from '../../utils/exportFontEmbed';
import { gateDownload, confirmDownload } from '../../lib/downloadGate';
import { applyWatermark } from '../../lib/watermark';

const DownloadDialog = ({
  open,
  onOpenChange,
  pages,
  currentPageId,
  canvasSize,
  canvasRef,
  elements,
  canvasBackground,
  renderElementsForExport,
  onSwitchPage,
  // Shared-template-session integration (Phase E):
  //   templateId       — original template id when the design has provenance
  //   sessionType      — "shared_session" | "user_copy" | null
  //   onQuotaExceeded  — callback to open the Upgrade modal (parent controls modal state)
  //   onDownloadsDisabled — callback when share_settings.allow_downloads=false
  templateId = null,
  sessionType = null,
  onQuotaExceeded,
  onDownloadsDisabled,
}) => {
  const { toast } = useToast();
  const safePages = Array.isArray(pages) ? pages : [];
  const safeCurrentId = safePages.length > 0 && currentPageId
    ? (safePages.some(p => p.id === currentPageId) ? currentPageId : safePages[0].id)
    : safePages[0]?.id ?? null;

  const [format, setFormat] = useState('png');
  const [quality, setQuality] = useState(100);
  const [transparentBg, setTransparentBg] = useState(false);
  const [pageSelection, setPageSelection] = useState('current');
  const [selectedPages, setSelectedPages] = useState(() => (safeCurrentId ? [safeCurrentId] : []));
  const [pageRange, setPageRange] = useState({ from: 1, to: Math.max(1, safePages.length) });
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

  // Sync selection when dialog opens or pages/currentPageId change
  useEffect(() => {
    if (!open) return;
    const currentId = safeCurrentId || safePages[0]?.id;
    if (!currentId) {
      setSelectedPages([]);
      setPageRange({ from: 1, to: Math.max(1, safePages.length) });
      return;
    }
    setPageRange(prev => {
      const { from, to } = validatePageRange(prev.from, prev.to, safePages.length);
      return { from, to };
    });
    setSelectedPages(prev => {
      const valid = prev.filter(id => safePages.some(p => p.id === id));
      if (valid.length === 0) return [currentId];
      return valid;
    });
  }, [open, safeCurrentId, safePages.length, safePages]);

  const formatOptions = [
    { value: 'png', label: 'PNG', description: 'Best for web, supports transparency', icon: FileImage },
    { value: 'jpeg', label: 'JPEG', description: 'Smaller file size, no transparency', icon: Image },
    { value: 'pdf', label: 'PDF', description: 'Best for printing and documents', icon: FileText },
  ];

  const handlePageSelectionChange = (value) => {
    setPageSelection(value);
    if (value === 'current') {
      setSelectedPages(safeCurrentId ? [safeCurrentId] : []);
    } else if (value === 'all') {
      setSelectedPages(safePages.map(p => p.id));
    } else if (value === 'range') {
      const { from, to } = validatePageRange(pageRange.from, pageRange.to, safePages.length);
      const rangePages = safePages.slice(from - 1, to).map(p => p.id);
      setSelectedPages(rangePages);
    }
  };

  const handlePageToggle = (pageId) => {
    setSelectedPages(prev => {
      if (prev.includes(pageId)) {
        return prev.filter(id => id !== pageId);
      }
      return [...prev, pageId];
    });
    setPageSelection('custom');
  };

  const handleRangeChange = (type, value) => {
    const num = parseInt(value, 10) || 1;
    const next = type === 'from' ? { ...pageRange, from: num } : { ...pageRange, to: num };
    const { from, to } = validatePageRange(next.from, next.to, safePages.length);
    setPageRange({ from, to });
    const rangePages = safePages.slice(from - 1, to).map(p => p.id);
    setSelectedPages(rangePages);
  };

  const captureCanvas = async (pageData, isTransparent) => {
    // Create a temporary container for rendering
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'fixed';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    tempContainer.style.width = `${canvasSize.width}px`;
    tempContainer.style.height = `${canvasSize.height}px`;
    tempContainer.style.background = isTransparent ? 'transparent' : (pageData.background || '#ffffff');
    tempContainer.style.overflow = 'hidden';
    tempContainer.style.transform = 'none';
    document.body.appendChild(tempContainer);

    // Render elements for this page
    if (renderElementsForExport) {
      const elementsHtml = await renderElementsForExport(pageData.elements || elements.filter(e => e.pageId === pageData.id));
      tempContainer.innerHTML = elementsHtml;
    } else {
      // Fallback: clone the current canvas
      const canvas = canvasRef?.current;
      if (canvas) {
        const clone = canvas.cloneNode(true);
        clone.style.transform = 'none';
        clone.style.position = 'relative';
        clone.style.left = '0';
        clone.style.top = '0';
        clone.style.width = `${canvasSize.width}px`;
        clone.style.height = `${canvasSize.height}px`;
        if (isTransparent) {
          clone.style.background = 'transparent';
        }
        tempContainer.appendChild(clone);
      }
    }

    try {
      const canvas = await html2canvas(tempContainer, {
        width: canvasSize.width,
        height: canvasSize.height,
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: false,
        backgroundColor: isTransparent ? null : (pageData.background?.startsWith('url') ? '#ffffff' : pageData.background || '#ffffff'),
        logging: false,
        windowWidth: canvasSize.width,
        windowHeight: canvasSize.height
      });
      
      document.body.removeChild(tempContainer);
      return canvas;
    } catch (error) {
      document.body.removeChild(tempContainer);
      throw error;
    }
  };

  const captureCurrentCanvas = async (isTransparent) => {
    const canvas = canvasRef?.current;
    if (!canvas) return null;

    const width = Math.max(1, Math.floor(Number(canvasSize?.width)) || 1080);
    const height = Math.max(1, Math.floor(Number(canvasSize?.height)) || 1080);
    const safeDim = getSafeExportDimensions(width, height);
    const scale = safeDim.ok ? 2 : Math.min(2, (safeDim.scale || 0.5) * 2);

    // Replay mode: opt-in deterministic export for repeatable captures
    // (animations paused, cursors hidden, hover disabled). Always paired
    // with teardown in a finally so the editor returns to interactive.
    const teardownReplay = (typeof window !== 'undefined' && window.__exportReplay)
      ? installReplayMode()
      : () => {};

    // Strict stabilization barrier — supersedes the previous fonts.ready/
    // rAF/setTimeout sequence. Now also awaits every image's decode(),
    // a second rAF for paint commit, and a microtask flush. After this
    // point getBoundingClientRect/getComputedStyle return values that
    // match what will actually be rasterized.
    await awaitExportStability(canvas);

    // Fetch + base64-encode every Google Font binary the page uses, so the
    // text-serialized SVGs (whose data-URL render context has no access to
    // the parent page's @font-face) can resolve their fonts inline. Cached
    // after the first run — subsequent exports resolve synchronously.
    await prepareEmbeddedFontCss();

    // Determinism snapshot — captures DPR, viewport, scroll, font readiness,
    // canvas bbox, transform, requested export dimensions, and a stateHash
    // used for cache invalidation. Logged under `[export-state]` when
    // diagnostics are on.
    const snapshot = captureExportState(canvas, width, height);

    // Find the clone of `canvas` inside the cloned document by its id —
    // html2canvas preserves id attributes on cloned subtrees.
    const canvasId = canvas.id || null;

    // Captured-by-reference results from the onclone fix-ups, read after
    // html2canvas returns so the parity assertions know what was/wasn't fixed.
    let clipFailures = [];
    let textSerializeResult = { converted: 0, failures: [], skippedGradient: 0 };

    let result;
    try {
      result = await html2canvas(canvas, {
      width,
      height,
      scale,
      useCORS: true,
      allowTaint: false,
      backgroundColor: isTransparent ? null : (canvasBackground?.startsWith?.('linear') || canvasBackground?.startsWith?.('url') ? '#ffffff' : canvasBackground || '#ffffff'),
      logging: false,
      windowWidth: width,
      windowHeight: height,
      x: 0,
      y: 0,
      scrollX: 0,
      scrollY: 0,
      // Parity fix-ups applied to the cloned DOM (the live tree is untouched):
      //   1. Zoom-freeze on the cloned canvas — pin to 1:1 canvasSize.
      //   2. inlineClipPathsForExport — DOMMatrix-flatten every
      //      `clip-path: url(#…)` SVG ref into a native CSS expression.
      //      Arcs are converted to cubic beziers first so non-rigid affines
      //      still flatten cleanly. Returns list of any unresolved refs.
      //   3. normalizeTextMetricsForExport — resolve `normal` keywords to
      //      browser-computed values; pin deterministic text-rendering hints.
      //   4. serializeTextElementsForExport — replace every text div with
      //      equivalent <svg><text>. Uses the same browser font metrics the
      //      editor renders at, so rasterization is renderer-equivalent.
      //      Order matters: text serialization runs LAST so the metric
      //      normalizer can read resolved values for the wrap measurement.
      onclone: (clonedDoc, clonedRoot) => {
        const root = clonedRoot || clonedDoc;
        const clonedCanvas = canvasId ? clonedDoc.getElementById(canvasId) : root;
        if (clonedCanvas && clonedCanvas.style) {
          clonedCanvas.style.transform = 'none';
          clonedCanvas.style.width  = `${width}px`;
          clonedCanvas.style.height = `${height}px`;
        }
        // Remove editor-only chrome (text-corruption warning icons, etc.)
        // from the cloned tree BEFORE any export step inspects it. Removing
        // rather than display:none-ing means downstream querySelectors don't
        // find these elements either — important because the text serializer
        // skips text whose subtree contains any <svg>, and the lucide icon
        // inside each warning indicator is itself an SVG.
        root.querySelectorAll('[data-export-hide]').forEach((n) => {
          if (n.parentNode) n.parentNode.removeChild(n);
        });
        // Pair each cloned <img> with its live twin while the two trees
        // still match — the clip-path bridge below replaces masked images.
        let imagePairs = [];
        try { imagePairs = pairImagesForExport(root, canvas); }
        catch (e) { console.warn('[export] image pairing failed:', e); }
        // Pass the LIVE canvas so the clip-path bridge can pre-render each
        // masked <img> from the already-decoded source bitmap.
        try { clipFailures = inlineClipPathsForExport(root, canvas) || []; }
        catch (e) { console.warn('[export] clip-path inline failed:', e); }
        // html2canvas ignores `object-fit` (it would stretch every cover-fit
        // photo to its frame) and paints an opaque box for an inline SVG
        // that carries a CSS filter: give the photos their real box and
        // take the filter off SVG roots, in the clone only.
        try { inlineObjectFitForExport(imagePairs); stripSvgRootFiltersForExport(root); }
        catch (e) { console.warn('[export] object-fit inline failed:', e); }
        try { normalizeTextMetricsForExport(canvas, root); }
        catch (e) { console.warn('[export] text-metric normalize failed:', e); }
        // Text-as-SVG can be opted out via `window.__exportTextAsSVG = false`
        // for debugging the html2canvas-text path. Default ON for parity.
        // Pass the LIVE canvas as the source of truth for actual line breaks:
        // the serializer reads Range.getBoundingClientRect() from each live
        // text element rather than guessing wrap via canvas2d.measureText.
        const textAsSVG = typeof window === 'undefined' || window.__exportTextAsSVG !== false;
        if (textAsSVG) {
          try { textSerializeResult = serializeTextElementsForExport(canvas, root); }
          catch (e) { console.warn('[export] text-serialize failed:', e); }

          // Inject the embedded @font-face CSS into every SVG <text> export
          // (gradient + svg-word strategies). An SVG-root <style> cascades to
          // SVG <text> correctly. The foreignObject strategy embeds its fonts
          // INSIDE the foreignObject HTML instead (see exportTextForeignObject),
          // because an SVG-root <style> does NOT reach foreignObject HTML when
          // the SVG is rasterized as a data-URL image.
          //
          // Per-<text> filtering: inject ONLY that text's family/weight/style,
          // not the full multi-MB catalog — otherwise every gradient/svg-word
          // SVG carries the whole embed and the export crawls.
          if (getCachedEmbeddedFontCss()) {
            const SVG_NS = 'http://www.w3.org/2000/svg';
            root.querySelectorAll('svg').forEach((svg) => {
              const textEl = svg.querySelector('text');
              if (!textEl) return;
              const fontCss = getEmbeddedFontCssFor(
                textEl.getAttribute('font-family'),
                textEl.getAttribute('font-weight'),
                textEl.getAttribute('font-style'),
              );
              if (!fontCss) return;
              const styleEl = clonedDoc.createElementNS(SVG_NS, 'style');
              styleEl.textContent = fontCss;
              svg.insertBefore(styleEl, svg.firstChild);
            });
          }
        }
      },
      });
    } finally {
      // Replay mode teardown always runs, even on html2canvas failure,
      // so the editor returns to interactive state. No-op when not in replay.
      teardownReplay();
    }

    // Optional diagnostics + parity assertions: dev-only, gated on
    // `window.__exportDiagnostics`. Set `window.__exportStrict = true` to
    // throw on parity violations instead of just logging.
    if (typeof window !== 'undefined' && window.__exportDiagnostics) {
      // Cache invalidation: verify the snapshot's stateHash still matches
      // the current environment. If anything shifted (font load, zoom, DPR,
      // viewport, canvas size), the snapshot's measurements are stale and
      // parity assertions should NOT be trusted.
      const freshness = verifySnapshotFresh(canvas, snapshot);
      if (freshness.stale) {
        console.warn('[export-state] snapshot stale — environment changed mid-export', freshness);
      }

      let wholeDiff = null;
      let perElementDiff = null;
      try {
        wholeDiff = await computeExportVisualDiff(canvas, result, { tolerance: 12, sample: 2 });
        if (wholeDiff) console.log('[export-diff]', wholeDiff);
      } catch (e) { console.warn('[export-diff] failed:', e); }
      try {
        const pageElements = (Array.isArray(pages) ? pages.find(p => p.id === currentPageId)?.elements : null) || elements || [];
        perElementDiff = await diffExportByElement(canvas, result, pageElements, { width, height }, { tolerance: 12, sample: 2 });
        if (perElementDiff) {
          const top = perElementDiff.slice(0, 10).map(e => ({
            id: e.id, type: e.type, diffRatio: +e.diffRatio.toFixed(4), bbox: e.bbox,
          }));
          console.log('[export-diff:per-element top10]', top);
        }
      } catch (e) { console.warn('[export-diff:per-element] failed:', e); }

      if (textSerializeResult.converted > 0 || textSerializeResult.failures.length > 0) {
        console.log('[export-text-svg]', textSerializeResult);
      }

      // Automatic per-node text-retry pipeline.
      //   - Skips entirely when no text node exceeds the threshold
      //     (zero overhead in the common case).
      //   - When triggered: per failing node, render JUST that element
      //     with foreignObject → composite onto pass-1 → measure → if
      //     still failing, escalate to foreignObject-clipped. Never
      //     re-runs html2canvas over the full canvas.
      //   - Capped by retry budget + time budget; rotated elements
      //     deferred (logged) until composite math accounts for rotation.
      //   - Winning strategy cached by element signature so future
      //     exports skip the escalation.
      //   - Opt-out via `window.__exportAutoRetry = false`.
      const autoRetryEnabled = window.__exportAutoRetry !== false;
      if (autoRetryEnabled && perElementDiff) {
        try {
          // Reference canvas at the same scale as the export — shared
          // input for post-retry diff measurement so each attempt knows
          // whether it actually improved the node or not.
          const refCanvas = await html2canvas(canvas, {
            width, height, scale,
            useCORS: true, logging: false,
            backgroundColor: '#ffffff',
          });
          const retry = await autoRetryFailingTextElements({
            pass1Canvas: result,
            perElementDiff,
            perTextResults: textSerializeResult.perText,
            liveCanvas: canvas,
            exportScale: scale,
            liveReferenceCanvas: refCanvas,
            thresholds: { textDiffRatio: 0.02 },
          });
          // Per-attempt diagnostic — emits one log line per attempt per
          // node with `{id, attempt, strategy, diff, accepted}` shape
          // matching the brief's required format.
          for (const a of retry.attempts) console.log('[text-export]', a);
          // Memory: release the reference canvas immediately.
          refCanvas.width = 0;
          refCanvas.height = 0;
        } catch (e) { console.warn('[text-export auto-retry] failed:', e); }
      }

      // Coarse pHash fingerprint — comparable across runs of the same
      // scene to detect renderer regressions. Logged here so dev tooling
      // can copy/paste it into the regression-scenes golden table.
      try {
        const fp = fingerprintCanvas(result);
        if (fp) console.log('[export-fingerprint]', { length: fp.length, sample: fp.slice(0, 16) });
      } catch (e) { console.warn('[export-fingerprint] failed:', e); }

      // Skip parity assertions when the snapshot is stale — they'd be
      // comparing against measurements taken under different conditions.
      if (!freshness.stale) {
        assertExportParity({
          snapshot,
          exportedCanvas: result,
          clipFailures,
          textFailures: textSerializeResult.failures,
          wholeDiff,
          perElementDiff,
          clonedCanvasEl: canvas,
        });
      }
    }

    return result;
  };

  const downloadImage = (canvas, filename, format, quality) => {
    const link = document.createElement('a');
    link.download = filename;

    if (format === 'jpeg') {
      link.href = canvas.toDataURL('image/jpeg', quality / 100);
    } else {
      link.href = canvas.toDataURL('image/png');
    }

    link.click();
  };

  // Maps the dialog's format ('png' | 'jpeg' | 'pdf') to the analytics
  // event format ('png' | 'jpg'). PDF is skipped from analytics in the
  // shared-session quota path (per spec: PNG/JPG only are gated).
  const _analyticsFormat = (f) => (f === 'jpeg' ? 'jpg' : f === 'png' ? 'png' : null);

  const downloadPDF = async (canvases, filename) => {
    if (!canvases.length) return;
    const pdfWidth = Math.max(1, Math.floor(Number(canvasSize?.width)) || 1080);
    const pdfHeight = Math.max(1, Math.floor(Number(canvasSize?.height)) || 1080);
    const q = Math.max(0.1, Math.min(1, Number(quality) / 100)) || 1;

    const pdf = new jsPDF({
      orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
      unit: 'px',
      format: [pdfWidth, pdfHeight],
      hotfixes: ['px_scaling']
    });

    canvases.forEach((canvas, index) => {
      if (index > 0) {
        pdf.addPage([pdfWidth, pdfHeight], pdfWidth > pdfHeight ? 'landscape' : 'portrait');
      }
      const imgData = canvas.toDataURL('image/jpeg', q);
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
    });

    pdf.save(filename);
  };

  const handleDownload = async () => {
    const pagesToDownload = getPagesToDownload(safePages, selectedPages);
    if (pagesToDownload.length === 0) {
      toast({
        title: 'No pages selected',
        description: 'Select at least one page to download.',
        variant: 'destructive',
      });
      return;
    }
    if (!canvasRef?.current) {
      toast({
        title: 'Canvas not ready',
        description: 'Please wait for the design to load and try again.',
        variant: 'destructive',
      });
      return;
    }

    // ── Pre-flight gate for image exports (PNG / JPG) ─────────────────────
    // PDF goes through unchecked: the spec gates PNG/JPG only. Scratch
    // designs (no templateId) also bypass the gate — gateDownload returns
    // allowed=true with no watermark/quota when templateId is falsy.
    let gate = { allowed: true, applyWatermark: false, maxDimension: null };
    const analyticsFormat = _analyticsFormat(format);
    if (analyticsFormat && templateId) {
      gate = await gateDownload({
        templateId, format: analyticsFormat, sessionType,
      });
      if (!gate.allowed) {
        if (gate.reason === 'quota_exceeded') {
          onQuotaExceeded && onQuotaExceeded();
        } else if (gate.reason === 'downloads_disabled') {
          if (onDownloadsDisabled) onDownloadsDisabled();
          else toast({
            title: 'Downloads disabled',
            description: 'The template owner has disabled downloads.',
            variant: 'destructive',
          });
        } else if (gate.reason === 'unauthenticated') {
          toast({
            title: 'Sign in required',
            description: 'Please sign in to download.',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Download unavailable',
            description: 'Could not start the download. Try again.',
            variant: 'destructive',
          });
        }
        return;
      }
    }

    setIsDownloading(true);
    setDownloadProgress({ current: 0, total: pagesToDownload.length });
    const originalPageId = safeCurrentId;

    try {
      if (format === 'pdf') {
        const canvases = [];
        let currentViewPageId = originalPageId;
        for (let i = 0; i < pagesToDownload.length; i++) {
          setDownloadProgress({ current: i + 1, total: pagesToDownload.length });
          if (pagesToDownload[i].id !== currentViewPageId && onSwitchPage) {
            onSwitchPage(pagesToDownload[i].id);
            await new Promise(resolve => setTimeout(resolve, 300));
            currentViewPageId = pagesToDownload[i].id;
          }
          const canvas = await captureCurrentCanvas(false);
          if (canvas) {
            canvases.push(canvas);
          } else {
            toast({
              title: 'Capture failed',
              description: `Could not capture page ${i + 1}. Try again.`,
              variant: 'destructive',
            });
            if (onSwitchPage) onSwitchPage(originalPageId);
            return;
          }
        }
        if (originalPageId !== pagesToDownload[pagesToDownload.length - 1]?.id && onSwitchPage) {
          onSwitchPage(originalPageId);
        }
        if (canvases.length > 0) {
          const filename = getDownloadFilename('design', 'pdf', pagesToDownload.length > 1, pagesToDownload.length);
          await downloadPDF(canvases, filename);
          toast({ title: 'Download complete', description: 'PDF saved successfully.' });
        }
      } else {
        let successCount = 0;
        let currentViewPageId = originalPageId;
        for (let i = 0; i < pagesToDownload.length; i++) {
          setDownloadProgress({ current: i + 1, total: pagesToDownload.length });
          if (pagesToDownload[i].id !== currentViewPageId && onSwitchPage) {
            onSwitchPage(pagesToDownload[i].id);
            await new Promise(resolve => setTimeout(resolve, 300));
            currentViewPageId = pagesToDownload[i].id;
          }
          const rawCanvas = await captureCurrentCanvas(transparentBg && format === 'png');
          if (rawCanvas) {
            let exportCanvas = rawCanvas;
            if (gate.applyWatermark) {
              try {
                exportCanvas = await applyWatermark(rawCanvas, {
                  maxDimension: gate.maxDimension,
                });
              } catch {
                exportCanvas = rawCanvas; // watermark failure must not block export
              }
            }
            const pageIndex = safePages.findIndex(p => p.id === pagesToDownload[i].id) + 1;
            const filename = getDownloadFilename('design', format, pagesToDownload.length > 1, pageIndex);
            downloadImage(exportCanvas, filename, format, quality);
            successCount++;
            // Post-flight: record analytics ONLY after a successful save.
            if (analyticsFormat && templateId) {
              await confirmDownload({
                templateId, format: analyticsFormat, sessionType,
              });
            }
          }
          if (i < pagesToDownload.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        if (originalPageId !== pagesToDownload[pagesToDownload.length - 1]?.id && onSwitchPage) {
          onSwitchPage(originalPageId);
        }
        if (successCount > 0) {
          toast({
            title: 'Download complete',
            description: successCount === pagesToDownload.length
              ? `${successCount} file(s) saved.`
              : `${successCount} of ${pagesToDownload.length} file(s) saved.`,
          });
        } else {
          toast({
            title: 'Capture failed',
            description: 'Could not capture the design. Try again.',
            variant: 'destructive',
          });
        }
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download failed',
        description: error?.message || 'Something went wrong. Try again.',
        variant: 'destructive',
      });
      if (onSwitchPage) onSwitchPage(originalPageId);
    } finally {
      setIsDownloading(false);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-indigo-600" />
            Download Design
          </DialogTitle>
          <DialogDescription>
            Choose format and pages to download
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Format Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">File Format</Label>
            <div className="grid grid-cols-3 gap-3">
              {formatOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={format === option.value}
                    aria-label={`Select format ${option.label}`}
                    onClick={() => setFormat(option.value)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      format === option.value
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className={`w-6 h-6 mx-auto mb-2 ${format === option.value ? 'text-indigo-600' : 'text-gray-500'}`} />
                    <div className={`text-sm font-medium ${format === option.value ? 'text-indigo-600' : 'text-gray-700'}`}>
                      {option.label}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quality Slider (for JPEG) */}
          {format === 'jpeg' && (
            <div className="space-y-3">
              <div className="flex justify-between">
                <Label className="text-sm font-semibold">Quality</Label>
                <span className="text-sm text-gray-500">{quality}%</span>
              </div>
              <Slider
                value={[quality]}
                onValueChange={([value]) => setQuality(value)}
                min={10}
                max={100}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>Smaller file</span>
                <span>Better quality</span>
              </div>
            </div>
          )}

          {/* Transparent Background (for PNG) */}
          {format === 'png' && (
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-sm font-semibold">Transparent Background</Label>
                <p className="text-xs text-gray-500">Remove background color</p>
              </div>
              <Checkbox
                checked={transparentBg}
                onCheckedChange={setTransparentBg}
              />
            </div>
          )}

          {/* Page Selection */}
          {safePages.length > 1 && (
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Pages to Download</Label>
              <RadioGroup value={pageSelection} onValueChange={handlePageSelectionChange} className="space-y-2">
                <div className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="current" id="current" />
                  <Label htmlFor="current" className="flex-1 cursor-pointer">
                    Current page only
                    <span className="text-xs text-gray-500 ml-2">
                      (Page {safePages.findIndex(p => p.id === safeCurrentId) + 1 || 1})
                    </span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="all" id="all" />
                  <Label htmlFor="all" className="flex-1 cursor-pointer">
                    All pages
                    <span className="text-xs text-gray-500 ml-2">({safePages.length} pages)</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="range" id="range" />
                  <Label htmlFor="range" className="cursor-pointer">Page range</Label>
                  {pageSelection === 'range' && (
                    <div className="flex items-center gap-2 ml-4">
                      <Input
                        type="number"
                        value={pageRange.from}
                        onChange={(e) => handleRangeChange('from', e.target.value)}
                        className="w-16 h-8"
                        min={1}
                        max={safePages.length}
                      />
                      <span className="text-gray-500">to</span>
                      <Input
                        type="number"
                        value={pageRange.to}
                        onChange={(e) => handleRangeChange('to', e.target.value)}
                        className="w-16 h-8"
                        min={1}
                        max={safePages.length}
                      />
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="custom" id="custom" />
                  <Label htmlFor="custom" className="cursor-pointer">Select specific pages</Label>
                </div>
              </RadioGroup>

              {/* Custom Page Selection */}
              {pageSelection === 'custom' && (
                <div className="grid grid-cols-4 gap-2 mt-3 p-3 bg-gray-50 rounded-lg max-h-32 overflow-y-auto">
                  {safePages.map((page, index) => (
                    <button
                      key={page.id}
                      onClick={() => handlePageToggle(page.id)}
                      className={`p-2 rounded-md text-sm font-medium transition-all ${
                        selectedPages.includes(page.id)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white border border-gray-200 text-gray-700 hover:border-indigo-300'
                      }`}
                    >
                    {index + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Download Summary */}
          <div className="p-3 bg-indigo-50 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-indigo-600" />
              <span className="text-indigo-800">
                {format === 'pdf' 
                  ? `Download ${selectedPages.length} page${selectedPages.length > 1 ? 's' : ''} as single PDF`
                  : `Download ${selectedPages.length} ${format.toUpperCase()} file${selectedPages.length > 1 ? 's' : ''}`
                }
                {transparentBg && format === 'png' && ' with transparent background'}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDownloading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDownload} 
            disabled={isDownloading || selectedPages.length === 0 || safePages.length === 0}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {isDownloading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Downloading {downloadProgress.current}/{downloadProgress.total}...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadDialog;
